/**
 * Browser-side redaction for the Leaps analytics SDK.
 *
 * Everything in this file runs inside the VISITOR's browser, before anything is
 * sent to Leaps. The rule is: a visitor's own input must never leave their
 * machine. Heatmaps only need layout, so we keep structure/CSS and throw away
 * values.
 *
 * Used by src/lib/tracker/index.ts (bundled into public/qct.js by
 * scripts/build-sdk.js). Keep it dependency-free and ES2015-compatible.
 */

/** What a redacted value is replaced with. Constant length, so it leaks nothing. */
export const REDACTED = '[redacted]';

/** Query-string safe variant (no brackets). */
export const REDACTED_PARAM = 'redacted';

/**
 * Regions a customer has marked as never-record.
 * `data-leaps-ignore` is the documented marker and the one the server-side
 * Puppeteer capture (api/analytics/snapshot/capture) already honours;
 * `data-qct-ignore` is accepted as a legacy alias.
 */
export const IGNORE_SELECTOR = '[data-leaps-ignore],[data-qct-ignore]';

/** Attributes kept on a marked element's emptied placeholder (layout only). */
const IGNORED_KEEP_ATTRS = [
  'class',
  'style',
  'id',
  'width',
  'height',
  'type',
  'data-leaps-ignore',
  'data-qct-ignore',
];

/** Input types whose value is dropped outright - not even a placeholder. */
const DROP_VALUE_TYPES = ['password', 'hidden', 'file'];

/** Input types whose `value` is the visible button label, not visitor input. */
const LABEL_VALUE_TYPES = ['button', 'submit', 'reset', 'image'];

/** Elements removed wholesale: they can carry data and never affect layout. */
const DROPPED_TAGS = 'script,noscript,template';

/** URL-bearing attributes that get query-string redaction, by tag name. */
const URL_ATTRS: { [tag: string]: string[] } = {
  a: ['href'],
  area: ['href'],
  form: ['action'],
  button: ['formaction'],
  input: ['formaction'],
  iframe: ['src'],
  embed: ['src'],
  object: ['data'],
};

/** Meta tags whose `content` is a secret rather than page metadata. */
const SECRET_META = /csrf|xsrf|token|auth|session|secret|api[-_]?key|nonce/;

/**
 * Parameter names (lower-cased, letters+digits only) that are always redacted.
 * Short names live here rather than in the parts list below, because as parts
 * they would also match harmless names ('pan' in 'company', 'upi' in 'groupid',
 * 'key' in 'keyword', 'tel' in 'hotel').
 */
const SENSITIVE_PARAM_EXACT = [
  'key', 'sig', 'sid', 'code', 'state', 'pass', 'pin', 'phone', 'mobile', 'tel',
  'mail', 'card', 'cvv', 'cvc', 'ssn', 'pan', 'dob', 'otp', 'token', 'email',
  'password', 'secret', 'auth', 'jwt', 'username', 'aadhaar',
  'ph', 'mob', 'cc', 'ccv', 'pw', 'upi', 'upiid', 'vpa', 'ifsc', 'iban', 'acc',
  'acct', 'account', 'name',
];

/** Substrings that mark a parameter name as sensitive. */
const SENSITIVE_PARAM_PARTS = [
  'token', 'otp', 'passw', 'pwd', 'secret', 'apikey', 'auth', 'signature',
  'session', 'jwt', 'bearer', 'email', 'credential', 'passcode', 'passkey',
  'aadha', 'adhaar', 'adhar', 'cardnumber', 'cvv', 'cvc', 'ssn', 'magic',
  'oauth', 'verifycode', 'verificationcode', 'resetcode', 'accesskey',
  'privatekey', 'onetimecode', 'accesscode', 'securitycode', 'smscode',
  'confirmcode', 'confirmationcode', 'invitecode', 'licensekey', 'licencekey',
  // Phone numbers: phone_number, contactPhone, mobile_no, whatsapp, msisdn
  'phone', 'mobile', 'whatsapp', 'msisdn', 'telno', 'telnumber',
  // Cards: card_no, cc_number, creditcard, cardholder
  'cardno', 'cardnum', 'ccno', 'ccnum', 'creditcard', 'debitcard', 'cardholder',
  // Bank, UPI and identity documents
  'accountno', 'accountnum', 'acctno', 'acctnum', 'accno', 'bankaccount',
  'ifsc', 'iban', 'upiid', 'upiaddress', 'upihandle', 'vpa', 'pannumber', 'panno',
  'pancard', 'passport',
  // A person's name, address and date of birth (your-name is Contact Form 7's default)
  'firstname', 'lastname', 'fullname', 'surname', 'yourname', 'customername',
  'contactname', 'clientname', 'billingname', 'shippingname', 'address', 'birth',
];

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
/** 10+ digits, optionally spaced/hyphenated: cards, phones, account numbers. */
const GROUPED_DIGITS_RE = /\d(?:[\s-]?\d){9,}/g;

/**
 * Yes/no twin of EMAIL_RE (a /g regex keeps state between .test() calls). The
 * match must start where a run of address characters starts, so each run is
 * scanned once: EMAIL_RE's unanchored form is quadratic, and these checks also
 * run on the server over addresses a client sent (100 KB took 16 s). No
 * lookbehind, which older Safari cannot parse.
 */
const EMAIL_TEST_RE = /(?:^|[^A-Za-z0-9._%+-])[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
/** A whole value that is a UPI ID ("ravi@okaxis") or an address with no dot in its domain. */
const HANDLE_VALUE_RE = /^[A-Za-z0-9._-]{2,256}@[A-Za-z][A-Za-z0-9]{1,63}$/;
/**
 * 10+ digits with at most two spaces, dots, hyphens or brackets between each:
 * phone numbers ("+91 98765 43210", "(987) 654-3210"), card numbers (13-19
 * digits), Aadhaar numbers (12) and bank account numbers.
 */
const LONG_NUMBER_TEST_RE = /\d(?:[\s().-]{0,2}\d){9,}/;

/** How deep a redirect-style value holding another address is followed. */
const MAX_NESTING = 2;
/** 6+ plain digits: one-time codes, PINs, long ids. */
const LONG_DIGITS_RE = /\d{6,}/g;
/** Long opaque strings that mix letters and digits: JWTs, API keys, nonces. */
const OPAQUE_TOKEN_RE = /[A-Za-z0-9_.-]{24,}/g;

function lower(value: string | null): string {
  return (value || '').toLowerCase();
}

function contains(list: string[], value: string): boolean {
  return list.indexOf(value) !== -1;
}

/* -------------------------------------------------------------------------- */
/* Text                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Scrub free text (a click label, an error message) of things that look like
 * personal data. This is best-effort pattern matching, not a guarantee - the
 * guarantee only exists for form fields, which are handled structurally below.
 */
export function scrubText(text: string | null | undefined, maxLength = 200): string {
  if (!text) return '';
  let out = String(text).slice(0, maxLength);
  out = out.replace(EMAIL_RE, REDACTED);
  out = out.replace(GROUPED_DIGITS_RE, REDACTED);
  out = out.replace(LONG_DIGITS_RE, REDACTED);
  out = out.replace(OPAQUE_TOKEN_RE, function (match) {
    // Only opaque if it actually mixes letters and digits (skips normal words,
    // long class names and dotted file paths that are all letters).
    if (!/[0-9]/.test(match) || !/[A-Za-z]/.test(match)) return match;
    return REDACTED;
  });
  return out;
}

/** Selectors only get e-mail addresses removed; digits are load-bearing there. */
export function scrubSelector(selector: string | null | undefined): string {
  if (!selector) return '';
  return String(selector).replace(EMAIL_RE, REDACTED);
}

/* -------------------------------------------------------------------------- */
/* URLs                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Decode a URL component for inspection. Repeats, because redirect-style
 * parameters are often encoded twice; stops at malformed encoding.
 */
function decodeForCheck(value: string): string {
  let out = value.replace(/\+/g, ' ');
  for (let i = 0; i < 3; i++) {
    let next: string;
    try {
      next = decodeURIComponent(out);
    } catch {
      break;
    }
    if (next === out) break;
    out = next;
  }
  return out;
}

/** True when text is itself personal data: an e-mail address, or a phone, card or Aadhaar-length number. */
function looksPersonal(decoded: string): boolean {
  return EMAIL_TEST_RE.test(decoded) || LONG_NUMBER_TEST_RE.test(decoded);
}

/**
 * The same for a query or hash value, which also counts a UPI ID ("pa=ravi@okaxis").
 * Not used on path segments, where "name@tag" is usually a package version.
 */
function looksPersonalValue(decoded: string): boolean {
  return looksPersonal(decoded) || HANDLE_VALUE_RE.test(decoded.trim());
}

function isSensitiveParamName(rawName: string): boolean {
  const normalized = decodeForCheck(rawName).toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!normalized) return false;
  if (contains(SENSITIVE_PARAM_EXACT, normalized)) return true;
  for (let i = 0; i < SENSITIVE_PARAM_PARTS.length; i++) {
    if (normalized.indexOf(SENSITIVE_PARAM_PARTS[i]) !== -1) return true;
  }
  return false;
}

/**
 * A value is redacted whatever its parameter is called when it holds an e-mail
 * address or a long number, or is a UPI ID. A value that is itself an address or a query string
 * (?next=%2Faccount%3Ftoken%3D...) is redacted inside.
 */
function redactParamValue(value: string, depth: number): string {
  if (!value) return value;
  const decoded = decodeForCheck(value);
  if (looksPersonalValue(decoded)) return REDACTED_PARAM;
  if (depth < MAX_NESTING && decoded.indexOf('=') !== -1) {
    const inner = decoded.indexOf('?') !== -1 || decoded.indexOf('#') !== -1
      ? redactUrlAt(decoded, depth + 1)
      : redactQueryString(decoded, depth + 1);
    if (inner !== decoded) return encodeURIComponent(inner);
  }
  return value;
}

function redactQueryString(query: string, depth: number): string {
  if (!query) return query;
  const pairs = query.split('&');
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i];
    const eq = pair.indexOf('=');
    if (eq <= 0) {
      // A bare value with no name ("?jane@example.com") can still be personal data.
      if (pair && looksPersonalValue(decodeForCheck(pair))) pairs[i] = REDACTED_PARAM;
      continue;
    }
    const name = pair.slice(0, eq);
    pairs[i] = name + '=' + (isSensitiveParamName(name)
      ? REDACTED_PARAM
      : redactParamValue(pair.slice(eq + 1), depth));
  }
  return pairs.join('&');
}

/**
 * Replace path segments that are themselves personal data - an e-mail address
 * or a phone, card or Aadhaar-length number (/users/jane@example.com,
 * wa.me/919876543210) - with "redacted". Every other segment is kept exactly,
 * because heatmaps are grouped by path.
 */
export function redactPath(path: string | null | undefined): string {
  if (!path) return '';
  const segments = String(path).split('/');
  for (let i = 0; i < segments.length; i++) {
    if (segments[i] && looksPersonal(decodeForCheck(segments[i]))) segments[i] = REDACTED_PARAM;
  }
  return segments.join('/');
}

/** Hash routes ("#/page?token=..."), hash parameters ("#access_token=...") and bare values ("#jane@example.com"). */
function redactHash(hash: string, depth: number): string {
  const innerQuery = hash.indexOf('?');
  if (innerQuery !== -1) {
    return redactPath(hash.slice(0, innerQuery)) + '?' + redactQueryString(hash.slice(innerQuery + 1), depth);
  }
  if (hash.indexOf('=') !== -1) return redactQueryString(hash, depth);
  return redactPath(hash);
}

function redactUrlAt(url: string, depth: number): string {
  // Drop user:password@ credentials from the authority.
  let value = url.replace(/^([A-Za-z][A-Za-z0-9+.-]*:\/\/)[^/?#@]*@/, '$1');

  let hash: string | null = null;
  const hashIndex = value.indexOf('#');
  if (hashIndex !== -1) {
    hash = value.slice(hashIndex + 1);
    value = value.slice(0, hashIndex);
  }

  let query: string | null = null;
  const queryIndex = value.indexOf('?');
  if (queryIndex !== -1) {
    query = value.slice(queryIndex + 1);
    value = value.slice(0, queryIndex);
  }

  // The scheme and host ("https://example.com", "mailto:", "tel:") are kept as
  // they are; only the path after them is checked.
  const origin = /^(?:[A-Za-z][A-Za-z0-9+.-]*:)?(?:\/\/[^/]*)?/.exec(value);
  const prefix = origin ? origin[0] : '';
  value = prefix + redactPath(value.slice(prefix.length));

  if (query !== null) value += '?' + redactQueryString(query, depth);
  if (hash !== null) value += '#' + redactHash(hash, depth);
  return value;
}

/**
 * Strip personal data and secrets out of a URL before it is sent: query and
 * hash values named like a token, code, password, e-mail, phone, card or bank
 * field, any value (or path segment) that is itself an e-mail address or a
 * phone/card-length number, and user:password credentials. Works on absolute
 * and relative URLs and never re-encodes the parts it leaves alone.
 */
export function redactUrl(url: string | null | undefined): string {
  if (!url) return '';
  return redactUrlAt(String(url), 0);
}

/* -------------------------------------------------------------------------- */
/* DOM                                                                         */
/* -------------------------------------------------------------------------- */

function removeAllChildren(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Empty a customer-marked region, keeping only the box so layout still lines up. */
function blankIgnored(el: Element): void {
  const attrs = Array.from(el.attributes);
  for (let i = 0; i < attrs.length; i++) {
    if (!contains(IGNORED_KEEP_ATTRS, attrs[i].name.toLowerCase())) {
      el.removeAttribute(attrs[i].name);
    }
  }
  removeAllChildren(el);
  el.setAttribute('data-leaps-redacted', 'ignored');
}

function removeComments(root: Element): void {
  const doc = root.ownerDocument;
  if (!doc || !doc.createTreeWalker) return;
  const walker = doc.createTreeWalker(root, 128 /* NodeFilter.SHOW_COMMENT */);
  const comments: Node[] = [];
  let node = walker.nextNode();
  while (node) {
    comments.push(node);
    node = walker.nextNode();
  }
  for (let i = 0; i < comments.length; i++) {
    const parent = comments[i].parentNode;
    if (parent) parent.removeChild(comments[i]);
  }
}

function scrubAttributes(el: Element): void {
  const tag = el.tagName.toLowerCase();
  const attrs = Array.from(el.attributes);
  for (let i = 0; i < attrs.length; i++) {
    const name = attrs[i].name;
    const lowered = name.toLowerCase();
    // Inline event handlers are executable code that can also embed state.
    if (lowered.indexOf('on') === 0 && lowered.length > 2) {
      el.removeAttribute(name);
      continue;
    }
    // srcdoc is a whole inline document we have not redacted.
    if (lowered === 'srcdoc') {
      el.removeAttribute(name);
    }
  }

  const urlAttrs = URL_ATTRS[tag];
  if (urlAttrs) {
    for (let i = 0; i < urlAttrs.length; i++) {
      const value = el.getAttribute(urlAttrs[i]);
      if (!value) continue;
      if (/^\s*javascript:/i.test(value)) {
        el.setAttribute(urlAttrs[i], '#');
      } else if (!/^\s*(data|blob):/i.test(value)) {
        el.setAttribute(urlAttrs[i], redactUrl(value));
      }
    }
  }

  if (tag === 'meta') {
    const descriptor = lower(el.getAttribute('name')) + ' ' +
      lower(el.getAttribute('property')) + ' ' +
      lower(el.getAttribute('http-equiv'));
    if (SECRET_META.test(descriptor) && el.getAttribute('content')) {
      el.setAttribute('content', REDACTED);
    }
  }
}

function redactInput(el: HTMLInputElement): void {
  const type = lower(el.getAttribute('type')) || 'text';

  if (type === 'checkbox' || type === 'radio') {
    // Keep only the checked state; the value can itself be personal data
    // (a radio list of saved e-mail addresses, for example).
    el.removeAttribute('value');
    let checked = el.hasAttribute('checked');
    try {
      checked = el.checked;
      el.value = '';
    } catch {
      /* ignore: some hosts make value read-only */
    }
    if (checked) el.setAttribute('checked', 'checked');
    else el.removeAttribute('checked');
    return;
  }

  if (contains(LABEL_VALUE_TYPES, type)) {
    // value is the button's visible label, written by the site author.
    return;
  }

  if (contains(DROP_VALUE_TYPES, type)) {
    // Passwords, hidden fields (CSRF tokens, cart state) and file names never
    // leave the browser - not even as a placeholder.
    el.removeAttribute('value');
    try {
      el.value = '';
    } catch {
      /* ignore */
    }
    el.setAttribute('data-leaps-redacted', 'value');
    return;
  }

  let hasValue = !!el.getAttribute('value');
  try {
    hasValue = hasValue || !!el.value;
  } catch {
    hasValue = true; // cannot read it: assume the worst and redact
  }
  if (!hasValue) return;

  el.setAttribute('value', REDACTED);
  try {
    el.value = REDACTED;
  } catch {
    /* ignore */
  }
  el.setAttribute('data-leaps-redacted', 'value');
}

function redactTextarea(el: HTMLTextAreaElement): void {
  let hasValue = !!(el.textContent && el.textContent.length);
  try {
    hasValue = hasValue || !!el.value;
  } catch {
    hasValue = true;
  }
  if (!hasValue) return;

  removeAllChildren(el);
  el.appendChild(el.ownerDocument.createTextNode(REDACTED));
  try {
    el.value = REDACTED;
  } catch {
    /* ignore */
  }
  el.setAttribute('data-leaps-redacted', 'value');
}

function redactSelect(el: HTMLSelectElement): void {
  const options = Array.from(el.querySelectorAll('option'));
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    let selected = option.hasAttribute('selected');
    try {
      selected = option.selected;
    } catch {
      /* ignore */
    }
    if (selected) {
      // The chosen option is what the visitor picked: blank its label and value
      // but keep it selected so the control still renders at the right size.
      removeAllChildren(option);
      option.appendChild(option.ownerDocument.createTextNode(REDACTED));
      if (option.hasAttribute('value')) option.setAttribute('value', REDACTED);
      option.setAttribute('selected', 'selected');
      option.setAttribute('data-leaps-redacted', 'value');
    } else {
      option.removeAttribute('selected');
    }
  }
}

function redactEditable(el: Element): void {
  if (!el.childNodes.length) return;
  removeAllChildren(el);
  const doc = el.ownerDocument;
  if (doc) el.appendChild(doc.createTextNode(REDACTED));
  el.setAttribute('data-leaps-redacted', 'content');
}

/**
 * Redact a DETACHED element tree in place.
 *
 * Never call this on the live document - `redactDocumentHtml` clones first so
 * the visitor's real page is untouched.
 */
export function redactTree(root: Element): void {
  // 1. Customer-marked regions first, so we never touch what is inside them.
  if (root.matches && root.matches(IGNORE_SELECTOR)) {
    blankIgnored(root);
    return;
  }
  const ignored = Array.from(root.querySelectorAll(IGNORE_SELECTOR));
  for (let i = 0; i < ignored.length; i++) blankIgnored(ignored[i]);

  // 2. Nodes that can carry data but never affect layout.
  const dropped = Array.from(root.querySelectorAll(DROPPED_TAGS));
  for (let i = 0; i < dropped.length; i++) {
    const parent = dropped[i].parentNode;
    if (parent) parent.removeChild(dropped[i]);
  }
  removeComments(root);

  // 3. Attributes: event handlers, srcdoc, URLs with secrets, secret metas.
  scrubAttributes(root);
  const all = Array.from(root.querySelectorAll('*'));
  for (let i = 0; i < all.length; i++) scrubAttributes(all[i]);

  // 4. Form fields and editable regions: the actual visitor input.
  const inputs = Array.from(root.querySelectorAll('input'));
  for (let i = 0; i < inputs.length; i++) redactInput(inputs[i] as HTMLInputElement);

  const textareas = Array.from(root.querySelectorAll('textarea'));
  for (let i = 0; i < textareas.length; i++) redactTextarea(textareas[i] as HTMLTextAreaElement);

  const selects = Array.from(root.querySelectorAll('select'));
  for (let i = 0; i < selects.length; i++) redactSelect(selects[i] as HTMLSelectElement);

  const editables = Array.from(root.querySelectorAll('[contenteditable],[role="textbox"]'));
  for (let i = 0; i < editables.length; i++) {
    const el = editables[i];
    if (lower(el.getAttribute('contenteditable')) === 'false') continue;
    redactEditable(el);
  }
}

/**
 * Serialise the page for a heatmap snapshot with every visitor-entered value
 * removed. Works on a clone, so the live page is never modified.
 */
export function redactDocumentHtml(doc?: Document): string {
  const source = doc || (typeof document !== 'undefined' ? document : null);
  if (!source || !source.documentElement) return '';
  const clone = source.documentElement.cloneNode(true) as HTMLElement;
  redactTree(clone);
  return clone.outerHTML;
}
